/**
 * Thin React reader over the shared Building Signaalit snapshot cache.
 * Prefer this from DataPointsExplorer; Config Signaalit keeps its own bundle.
 */
import { useEffect, useState } from "react";

import { useBuildingChangeTick } from "@/lib/building-changes";
import {
  getSharedBuildingSignaalitSnapshotCache,
  type BuildingSignaalitSnapshotBag,
} from "@/lib/building-signaalit-snapshot";

export type BuildingSignaalitSnapshotHookState =
  { status: "loading" } | { status: "ok"; bag: BuildingSignaalitSnapshotBag };

function hookStateFromCache(
  buildingId: string,
): BuildingSignaalitSnapshotHookState {
  const cached = getSharedBuildingSignaalitSnapshotCache().getState(buildingId);
  if (cached.status === "ok") {
    return { status: "ok", bag: cached.bag };
  }
  return { status: "loading" };
}

export function useBuildingSignaalitSnapshot(
  buildingId: string,
): BuildingSignaalitSnapshotHookState {
  const [trackedBuildingId, setTrackedBuildingId] = useState(buildingId);
  const [state, setState] = useState<BuildingSignaalitSnapshotHookState>(() =>
    hookStateFromCache(buildingId),
  );

  // Avoid one paint of the previous building's bag when buildingId changes.
  if (trackedBuildingId !== buildingId) {
    setTrackedBuildingId(buildingId);
    setState(hookStateFromCache(buildingId));
  }

  useEffect(() => {
    const cache = getSharedBuildingSignaalitSnapshotCache();
    const sync = () => setState(hookStateFromCache(buildingId));
    const unsubscribe = cache.subscribe(buildingId, sync);
    void cache.load(buildingId).then(sync);
    sync();
    return unsubscribe;
  }, [buildingId]);

  // New readings in this building re-read the live values in place (paced by the stream).
  const telemetryTick = useBuildingChangeTick(buildingId, "telemetry");
  useEffect(() => {
    if (telemetryTick === 0) return;
    void getSharedBuildingSignaalitSnapshotCache().refresh(buildingId);
  }, [buildingId, telemetryTick]);

  return state;
}
